import { useEffect, useRef, useState } from 'react'
import { message, Spin } from 'antd'
import { store, useAppDispatch } from 'store'
import {
  ensureProductClassesAsync,
  importedClassOptions,
  loadImportedPdfPolicyAsync,
  resolveImportedPolicyPlacement,
  type ImportedClassOption,
  type ImportedPolicyLoadResult,
  type ImportedPolicyPlacementMode,
} from 'slices/comparison'
import { ComparisonColumn } from 'types/enums'
import { IFeatureContents, IPolicy, ISelectedClassCover } from 'types/interfaces'
import { importPolicyPdf, type UploadedPolicyCheckResult } from '../ai-agent.service'
import { AttachmentIcon, UploadIllustration } from '../assets/svgs'
import { ImportedPolicyPicker } from './ImportedPolicyPicker'

const quoted = (names: string[]) => names.map((name) => `"${name}"`).join(', ')

const policyCount = (count: number) => `${count} ${count === 1 ? 'policy' : 'policies'}`

/**
 * What landed. One PDF wording can be sold by several insurers, so an upload can bring in
 * more than one policy at a time — the message has to say how many, and separate the ones
 * that took a new column from the ones that were already there and were refreshed in place.
 */
const importedMessage = ({ mode, addedNames, refreshedNames }: ImportedPolicyLoadResult) => {
  if (!addedNames.length) {
    // Nothing new: the table looks untouched, so say that rather than claiming an add.
    return refreshedNames.length === 1
      ? `"${refreshedNames[0]}" is already in your comparison — its wording has been refreshed.`
      : `${policyCount(refreshedNames.length)} from this wording are already in your ` +
          'comparison — they have been refreshed.'
  }

  const headline =
    addedNames.length === 1 ? 'Policy matched' : `${addedNames.length} policies matched`
  const outcome = mode === 'reset' ? 'loading comparison.' : 'added to your comparison.'
  const refreshedNote = refreshedNames.length
    ? ` ${policyCount(refreshedNames.length)} already in the comparison ` +
      `${refreshedNames.length === 1 ? 'was' : 'were'} refreshed.`
    : ''

  return `${headline} — ${outcome}${refreshedNote}`
}

/** The policies the upload matched but could not put on screen, and why. */
const skippedMessage = ({ skippedForLimit, skippedOutOfClass }: ImportedPolicyLoadResult) => {
  const notes: string[] = []

  if (skippedForLimit.length) {
    notes.push(
      `${quoted(skippedForLimit)} did not fit — a comparison holds up to ` +
        `${ComparisonColumn.MAX_SELECTED_LENGTH} policies. Remove a column and upload again.`,
    )
  }
  if (skippedOutOfClass.length) {
    const one = skippedOutOfClass.length === 1
    notes.push(
      `${quoted(skippedOutOfClass)} ${one ? 'belongs' : 'belong'} to a different class, so ` +
        `${one ? 'it cannot' : 'they cannot'} be compared alongside the rest of this wording.`,
    )
  }

  return notes.join(' ')
}

export interface ClassChange {
  from?: string
  to?: string
  /** Whether agreeing to it costs the user columns, as opposed to only changing the view. */
  clearsColumns: boolean
}

/**
 * Whether this import has to ask before it moves the comparison to another class, and what
 * agreeing to it costs.
 *
 * On the comparison page it is asked even with no columns filled: the user chose those
 * covers to look at, and an upload swapping the class out from under them is the same
 * surprise either way. With no cover selection at all there is nothing to change and
 * nothing to ask — the import is simply what opens the comparison.
 *
 * An import that starts a new comparison — the dashboard — is a fresh start, and a cover
 * selection left over from an earlier session is not something the user is looking at, so
 * naming it would be noise. Losing a comparison that still has columns in it is not noise,
 * though, and is still worth stopping for wherever the upload came from.
 */
export const resolveClassChange = (
  mode: ImportedPolicyPlacementMode,
  toClassName: string | undefined,
  currentCover: ISelectedClassCover | null,
  occupiedColumns: number,
  startsNewComparison = false,
): ClassChange | undefined => {
  if (mode !== 'reset') return undefined
  if (!currentCover?.coverId?.length) return undefined

  const clearsColumns = occupiedColumns > 0
  if (startsNewComparison && !clearsColumns) return undefined

  return { from: currentCover.className, to: toClassName, clearsColumns }
}

/**
 * What picking a class does to what is on screen: join the comparison, open one, switch
 * the cover selection, or clear the columns and start again.
 */
export type ClassOutcome = 'add' | 'open' | 'switch' | 'replace'

/** A matched upload waiting on the user, with everything the dialog shows about it. */
export interface PendingImport {
  fileName: string
  policies: IPolicy[]
  availableColumns: number
  policiesInComparison: number[]
  classChange?: ClassChange
  coverOptions: { id: number; name: string; inComparison: boolean }[]
  coverClassName?: string
  /** The classes the wording spans, the default first. One entry means no choice. */
  classOptions: (ImportedClassOption & { outcome: ClassOutcome })[]
  /** The class this import is placed in — the user's choice, or the default. */
  classId?: number | string
}

/**
 * What the dialog needs for one upload placed in one class. `classId` is the user's pick
 * for a wording that spans classes; without it the placement's own default stands.
 */
export const buildPendingImport = (
  comparisonState: ReturnType<typeof store.getState>['comparison'],
  fileName: string,
  policies: IPolicy[],
  startsNewComparison?: boolean,
  classId?: number | string,
): PendingImport => {
  const placement = resolveImportedPolicyPlacement(comparisonState, policies, classId)
  const occupiedColumns = comparisonState.selectedPolicyContents.length
  const currentCover = comparisonState.selectedProductClassCover

  const classChange = resolveClassChange(
    placement.mode,
    placement.className,
    currentCover,
    occupiedColumns,
    startsNewComparison,
  )

  // A reset clears the comparison first, so every column is free for this import.
  // Otherwise only what is left over from the current comparison is.
  const availableColumns =
    placement.mode === 'reset'
      ? ComparisonColumn.MAX_SELECTED_LENGTH
      : Math.max(0, ComparisonColumn.MAX_SELECTED_LENGTH - occupiedColumns)

  /**
   * The cover selection this import would leave behind, offered for the user to edit. On
   * the dashboard that is the wording's own covers; in a comparison it is what is already
   * loaded, plus anything the wording needs on top.
   *
   * `inComparison` is what lets the dialog show which of them are already on screen, so
   * adding a cover reads differently from keeping one.
   */
  const selectedCoverIds = currentCover?.coverId ?? []
  const coverOptions = placement.coverIds.map((id, index) => ({
    id,
    name: placement.coverTypes[index] || `Cover ${id}`,
    inComparison: selectedCoverIds.includes(id),
  }))

  return {
    fileName,
    policies,
    availableColumns,
    // A reset frees every column, so nothing counts as already on screen.
    policiesInComparison:
      placement.mode === 'reset'
        ? []
        : comparisonState.selectedPolicyContents.map((content: IFeatureContents) => content.id),
    classChange,
    coverOptions,
    coverClassName: placement.className,
    // Each option names its own consequence, so the cost of a choice is read before it is
    // made rather than in a warning that appears after.
    classOptions: importedClassOptions(comparisonState, policies).map((option) => {
      const optionPlacement = resolveImportedPolicyPlacement(comparisonState, policies, option.id)
      const change = resolveClassChange(
        optionPlacement.mode,
        option.name,
        currentCover,
        occupiedColumns,
        startsNewComparison,
      )
      let outcome: ClassOutcome = 'open'
      if (optionPlacement.mode !== 'reset') outcome = 'add'
      else if (change?.clearsColumns) outcome = 'replace'
      else if (change) outcome = 'switch'
      return { ...option, outcome }
    }),
    classId: placement.classId,
  }
}

export interface PolicyUploadOptions {
  /**
   * Runs once the imported policy is in the comparison. The chat box already sits on the
   * comparison page and needs nothing, but a caller elsewhere — the dashboard — has to
   * take the user there, or a successful import looks like it did nothing.
   */
  onImported?: () => void
  /**
   * Set where an upload opens a comparison rather than joining one already on screen — the
   * dashboard. It is a fresh start, so a cover selection left from an earlier session is
   * not worth naming; see resolveClassChange.
   */
  startsNewComparison?: boolean
}

export function usePolicyUpload({ onImported, startsNewComparison }: PolicyUploadOptions = {}) {
  const dispatch = useAppDispatch()
  const [showPopover, setShowPopover] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [isDragActive, setIsDragActive] = useState(false)
  const [uploadResult, setUploadResult] = useState<UploadedPolicyCheckResult | null>(null)
  /** A matched upload waiting on the user to say which of its policies to compare. */
  const [pendingImport, setPendingImport] = useState<PendingImport | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024

  useEffect(() => {
    if (!showPopover) return
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      if (
        popoverRef.current &&
        !popoverRef.current.contains(target) &&
        triggerRef.current &&
        !triggerRef.current.contains(target)
      ) {
        setShowPopover(false)
        setIsDragActive(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showPopover])

  /**
   * Puts the chosen policies into the comparison and says what happened. Separate from the
   * upload itself because the choice can be made in the picker, long after the response
   * came back.
   */
  const runImport = async (
    policies: IPolicy[],
    fileName: string,
    coverIds?: number[],
    classId?: number | string,
  ) => {
    const loadResult = await dispatch(
      loadImportedPdfPolicyAsync({ policies, coverIds, classId }),
    ).unwrap()

    if (!loadResult.added) {
      const maxPolicies = ComparisonColumn.MAX_SELECTED_LENGTH
      let blockedMessage: string
      if (loadResult.reason === 'no-available-covers') {
        blockedMessage =
          'This wording is written against covers that are no longer available, ' +
          'so it cannot be compared. Open a comparison first and upload it again ' +
          'to add it there.'
      } else if (loadResult.reason === 'out-of-class') {
        blockedMessage =
          'The policies on this wording belong to a different class to your current ' +
          'comparison, so they cannot be added to it.'
      } else if (loadResult.reason === 'no-policies') {
        blockedMessage = 'We could not read a policy from this wording. Please contact us.'
      } else {
        blockedMessage =
          `You can only compare up to ${maxPolicies} policies. ` +
          'Remove one before adding this wording.'
      }
      setUploadResult({ fileName, status: 'not_found', message: blockedMessage })
      message.warning(blockedMessage)
      return
    }

    const outcome = importedMessage(loadResult)
    setUploadResult({ fileName, status: 'matched', message: outcome })
    // Re-uploading a wording that already has its columns refreshes them in place, so
    // the table looks untouched — informational, not a fresh success.
    if (loadResult.alreadySelected) message.info(outcome)
    else message.success(outcome)

    // Some of the matched policies could not be shown. That is a partial result, not
    // a failure, so it goes out beside the success rather than replacing it.
    const skipped = skippedMessage(loadResult)
    if (skipped) message.warning(skipped)

    onImported?.()
  }

  const handlePickerConfirm = ({
    policies,
    coverIds,
  }: {
    policies: IPolicy[]
    coverIds: number[]
  }) => {
    const pending = pendingImport
    setPendingImport(null)
    if (pending) runImport(policies, pending.fileName, coverIds, pending.classId)
  }

  /**
   * The user picked another class for a wording that spans several. Everything the dialog
   * shows hangs off that choice — the covers, whether the comparison is cleared, how many
   * columns are free — so it is worked out again rather than patched.
   */
  const handleClassChange = (classId: number | string) => {
    if (!pendingImport) return
    setPendingImport(
      buildPendingImport(
        store.getState().comparison,
        pendingImport.fileName,
        pendingImport.policies,
        startsNewComparison,
        classId,
      ),
    )
  }

  const handleFilesUpload = async (fileList: FileList | null) => {
    if (isUploading) {
      message.warning('Please wait for the current upload to complete.')
      return
    }
    if (!fileList || fileList.length === 0) return
    if (fileList.length > 1) {
      message.error('Please upload only one file at a time.')
      setIsDragActive(false)
      return
    }
    const file = fileList[0]
    if (file.type !== 'application/pdf') {
      message.error('Please upload a PDF file')
      return
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      message.error('File is larger than 10MB. Please upload a smaller PDF.')
      return
    }
    setIsUploading(true)
    try {
      const result = await importPolicyPdf(file)
      if (result.status === 'found') {
        // The prompts below are decided by the same placement the thunk will resolve, so
        // they have to see the same catalogue. Without this they can be read off the seed
        // and then contradicted by what the load actually does.
        await dispatch(ensureProductClassesAsync())

        const pending = buildPendingImport(
          store.getState().comparison,
          result.fileName,
          result.policies,
          startsNewComparison,
        )

        /**
         * Everything an upload can change about the table is agreed to in one place. Where
         * there is a choice — which of several matched policies, which of the wording's
         * covers — the dialog asks it; where there is not, it confirms the single match and
         * the class it will load. Either way the user sees what was matched before the
         * comparison changes rather than after.
         *
         * With no column free there is nothing to confirm: the import cannot land, so it
         * falls through and is reported as the limit it is.
         */
        if (pending.availableColumns > 0) {
          setPendingImport(pending)
          return
        }

        await runImport(result.policies, result.fileName, undefined, pending.classId)
      } else if (result.status === 'rejected') {
        // The server's own wording — it explains a rule the user can act on, such as the
        // policy belonging to a different country to their account.
        setUploadResult({
          fileName: result.fileName,
          status: 'rejected',
          message: result.detail,
        })
        message.error(result.detail)
      } else if (result.status === 'unprocessable') {
        // The wording was recognised but the server could not build a policy from it.
        // The reason names internal ids, so it stays in the console.
        const unprocessableMessage = 'We could not process this policy. Please contact us.'
        setUploadResult({
          fileName: result.fileName,
          status: 'unprocessable',
          message: unprocessableMessage,
        })
        message.error(unprocessableMessage)
      } else if (result.status === 'not_found') {
        setUploadResult({
          fileName: result.fileName,
          status: 'not_found',
          message: 'Thanks — our team will review this wording and add it soon.',
        })
        message.info('Thanks — our team will review this wording and add it soon.')
      }
    } catch (error) {
      console.error(error)
      message.error('Upload failed. Please try again.')
    } finally {
      setIsUploading(false)
      setIsDragActive(false)
      setShowPopover(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  /**
   * Whether the drag carries files at all. Not everything dragged over a dropzone is an
   * upload: react-dnd's HTML5 backend reorders comparison columns with real drag events,
   * so a column dragged across the empty column header raised dragover here and lit it up
   * as "Drop to upload". Bailing out before preventDefault also leaves that drag to
   * react-dnd, which owns it — swallowing the event stopped the column being dropped.
   */
  const isFileDrag = (event: React.DragEvent<HTMLDivElement>) =>
    Array.from(event.dataTransfer?.types ?? []).includes('Files')

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    if (!isFileDrag(event)) return
    event.preventDefault()
    event.stopPropagation()
    if (isUploading) return
    setShowPopover(true)
    setIsDragActive(false)
    handleFilesUpload(event.dataTransfer.files)
  }

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    if (!isFileDrag(event)) return
    event.preventDefault()
    event.stopPropagation()
    if (isUploading) return
    if (!isDragActive) setIsDragActive(true)
  }

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    if (!isFileDrag(event)) return
    event.preventDefault()
    event.stopPropagation()
    if (isUploading) return
    // Always set false on leave, the overlay/drop logic will handle the rest
    // If we are dragging over children, React's event bubbling might still fire dragLeave for parent
    // Checking relatedTarget helps avoid flickering when entering children
    const nextTarget = event.relatedTarget as Node | null
    if (!nextTarget || !event.currentTarget.contains(nextTarget)) {
      setIsDragActive(false)
    }
  }

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isUploading) {
      message.warning('Please wait for the current upload to complete.')
      if (event.target) event.target.value = ''
      return
    }
    handleFilesUpload(event.target.files)
  }

  const togglePopover = () => {
    setShowPopover((prev) => !prev)
    setIsDragActive(false)
  }

  return {
    showPopover,
    setShowPopover,
    isUploading,
    isDragActive,
    setIsDragActive,
    uploadResult,
    fileInputRef,
    triggerRef,
    popoverRef,
    handleDrop,
    handleDragOver,
    handleDragLeave,
    handleFileInputChange,
    togglePopover,
    /**
     * The "which of these policies?" dialog, for the caller to render.
     *
     * It comes back as an element rather than as state because the three ways into this
     * hook — the chat popover, the dashboard dropzone and an empty comparison column —
     * each draw their own upload UI and only one of them renders PolicyUploadControl.
     * Returning the dialog itself is what stops a multi-policy upload from finishing
     * silently at the other two.
     */
    importPicker: pendingImport ? (
      // antd puts the dialog in a portal, but React still bubbles its events up this
      // component tree — into the dropzone that opens a file picker on click, and the
      // drag source the empty column sits in. Both would act on a click meant for the
      // dialog, so the events stop here.
      <div
        onClick={(event) => event.stopPropagation()}
        onMouseDown={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
      >
        <ImportedPolicyPicker
          open
          fileName={pendingImport.fileName}
          policies={pendingImport.policies}
          availableColumns={pendingImport.availableColumns}
          policiesInComparison={pendingImport.policiesInComparison}
          classChange={pendingImport.classChange}
          coverOptions={pendingImport.coverOptions}
          coverClassName={pendingImport.coverClassName}
          classOptions={pendingImport.classOptions}
          selectedClassId={pendingImport.classId}
          onClassChange={handleClassChange}
          onCancel={() => setPendingImport(null)}
          onConfirm={handlePickerConfirm}
        />
      </div>
    ) : null,
  }
}

interface PolicyUploadControlProps {
  variant?: 'icon' | 'bar' | 'toggle'
  uploadState?: ReturnType<typeof usePolicyUpload>
}

function PolicyUploadControl({ variant = 'icon', uploadState }: PolicyUploadControlProps) {
  const internalState = usePolicyUpload()
  const state = uploadState || internalState

  const {
    showPopover,
    isUploading,
    isDragActive,
    // uploadResult,
    fileInputRef,
    triggerRef,
    popoverRef,
    handleDrop,
    handleDragOver,
    handleDragLeave,
    handleFileInputChange,
    togglePopover,
    importPicker,
  } = state

  const triggerClasses = [
    'ai-agent-upload-trigger',
    variant === 'bar' ? 'ai-agent-upload-trigger--bar' : '',
    variant === 'toggle' ? 'ai-agent-upload-trigger--toggle' : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={`ai-agent-upload-control ai-agent-upload-control--${variant}`}>
      <button
        type='button'
        ref={triggerRef}
        className={`${triggerClasses}${isUploading ? ' is-uploading' : ''}`}
        onClick={togglePopover}
        disabled={isUploading}
        aria-label='Upload policy PDF'
        aria-expanded={showPopover}
        title='Upload policy PDF'
      >
        {isUploading ? <Spin size='small' /> : <AttachmentIcon aria-hidden='true' />}
        {variant === 'bar' && (
          <span className='ai-agent-upload-trigger-label'>
            {isUploading ? 'Uploading...' : 'Upload policy PDF'}
          </span>
        )}
        {variant === 'toggle' && (
          <span className='ai-agent-upload-trigger-label'>
            {isUploading ? 'Uploading...' : 'Upload policy PDF'}
          </span>
        )}
      </button>
      <div className={`ai-agent-upload-popover${showPopover ? ' is-visible' : ''}`}>
        <div
          className={`ai-agent-upload-card${isUploading ? ' is-uploading' : ''}`}
          ref={popoverRef}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className='ai-agent-upload-header'>Upload your Policy</div>
          <p className='ai-agent-upload-copy'>
            Please upload your policy file so AL can review and find from our records.
          </p>
          <div
            className={`ai-agent-upload-dropzone${isDragActive ? ' is-dragging' : ''}${
              isUploading ? ' is-uploading' : ''
            }`}
            onClick={() => {
              if (!isUploading) fileInputRef.current?.click()
            }}
          >
            {isUploading ? (
              <div className='ai-agent-upload-loading'>
                <Spin size='large' />
                <span>Processing your policy...</span>
              </div>
            ) : (
              <>
                <UploadIllustration className='ai-agent-upload-illustration' aria-hidden='true' />
                <div className='ai-agent-upload-dropzone-text'>
                  <strong>Drag & drop your policy PDF</strong>
                  <span className='ai-agent-upload-browse'>or browse files</span>
                  <span className='ai-agent-upload-support'>Supports PDF only (10MB max)</span>
                </div>
                <button
                  type='button'
                  className='ai-agent-upload-dropzone-action'
                  onClick={(event) => {
                    event.stopPropagation()
                    if (isUploading) return
                    if (fileInputRef.current) fileInputRef.current.value = ''
                    fileInputRef.current?.click()
                  }}
                  disabled={isUploading}
                >
                  Browse
                </button>
              </>
            )}
          </div>
          <input
            ref={fileInputRef}
            type='file'
            accept='application/pdf'
            className='ai-agent-upload-input'
            onChange={handleFileInputChange}
            hidden
          />
        </div>
      </div>
      {importPicker}
    </div>
  )
}

export { PolicyUploadControl }
